import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { api } from '../api/client.js';
import { isWorkerRole } from '../utils/roles.js';
import SupervisorBackLink from '../components/SupervisorBackLink.jsx';
import QuizStepProgress from '../components/QuizStepProgress.jsx';
import {
    OBSERVADOR_BODY_SIGNALS as bodySignals,
    OBSERVADOR_DOMAIN_OPTIONS as domainOptions,
    OBSERVADOR_REFLECTION_CHIPS,
    OBSERVADOR_SUPPORT_NEEDS as supportNeeds,
    OBSERVADOR_WORK_FRICTION as workFriction,
    checkinPhaseLabel,
} from '../data/observadorFormOptions.js';
import './Observador.css';

export default function Observador() {
    const { user } = useAuth();
    const [form, setForm] = useState({
        energyLevel: 4,
        bodySignal: 'estable',
        workFriction: 'baja',
        supportNeed: 'claridad',
        selectedDomain: '',
        reflectionText: '',
    });
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    function updateField(key, value) {
        setForm((current) => ({ ...current, [key]: value }));
    }

    function appendReflectionChip(text) {
        setForm((c) => ({
            ...c,
            reflectionText: c.reflectionText ? `${c.reflectionText.trim()} ${text}`.trim() : text,
        }));
    }

    function chipIsActive(text) {
        return form.reflectionText.includes(text);
    }

    async function handleSubmit(event) {
        event.preventDefault();
        setLoading(true);
        setError('');
        setResult(null);

        try {
            const body = await api.analyzeObservadorForm(form);
            if (!body?.success || !body.data) {
                setError(body?.error || 'No fue posible procesar el observador.');
                return;
            }
            setResult(body.data);
            setForm((current) => ({ ...current, reflectionText: '' }));
        } catch (err) {
            setError(err.message || 'No fue posible procesar el observador.');
        } finally {
            setLoading(false);
        }
    }

    const fmtRisk = (value) => `${Math.round(Number(value || 0) * 100)}%`;

    if (isWorkerRole(user?.rol)) {
        return (
            <div className="observador-page">
                <header className="observador-header">
                    <span>Pausa consciente</span>
                    <h1>Observador ALINEA</h1>
                    <p>
                        Para operarios, el mismo acompañamiento va integrado en <strong>Jornada</strong>: llegada, cierre y lectura
                        amable, sin repetir este formulario largo ni usar cámara aquí.
                    </p>
                </header>
                <section className="observador-card wide">
                    <p style={{ margin: '0 0 1rem', lineHeight: 1.55, color: '#334155' }}>
                        Vuelve a la pantalla principal y usa <strong>Jornada</strong> cuando quieras registrar cómo estás o ver la
                        lectura del día.
                    </p>
                    <Link to="/" className="observador-submit" style={{ display: 'inline-block', textAlign: 'center', textDecoration: 'none' }}>
                        Ir a Jornada
                    </Link>
                </section>
            </div>
        );
    }

    const isSupervisor = user?.rol === 'supervisor';

    return (
        <div className="observador-page">
            {isSupervisor && <SupervisorBackLink />}
            <header className="observador-header">
                <span>Pausa consciente</span>
                <h1>Observador ALINEA</h1>
                <p>
                    Te ayuda a ordenar cómo estás: energía, cuerpo, foco y una frase tuya. Se analizan señales léxicas simples al
                    momento; no guardamos tu texto completo ni audio ni vídeo: solo metadatos útiles para conversaciones humanas.
                </p>
            </header>

            <QuizStepProgress step={1} totalSteps={3} label="Tu check-in del día" />

            <form className="observador-layout" onSubmit={handleSubmit}>
                <section className="observador-card">
                    <div className="observador-section-title">
                        <h2>1. Cómo llegas</h2>
                        <p>Marca tu energía y cómo se siente el cuerpo ahora.</p>
                    </div>

                    <label className="range-field">
                        <span>Energía (del 1 al 6): {form.energyLevel}</span>
                        <input
                            type="range"
                            min="1"
                            max="6"
                            value={form.energyLevel}
                            onChange={(event) => updateField('energyLevel', Number(event.target.value))}
                        />
                    </label>

                    <div className="choice-grid">
                        {bodySignals.map((option) => (
                            <button
                                type="button"
                                key={option.value}
                                className={form.bodySignal === option.value ? 'selected' : ''}
                                onClick={() => updateField('bodySignal', option.value)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </section>

                <section className="observador-card">
                    <div className="observador-section-title">
                        <h2>2. Carga y apoyo</h2>
                        <p>Elige lo que más se parezca a hoy; orienta las sugerencias.</p>
                    </div>

                    <div className="field-row">
                        <label>
                            Carga en el trabajo
                            <select value={form.workFriction} onChange={(event) => updateField('workFriction', event.target.value)}>
                                {workFriction.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label>
                            Qué te ayudaría más
                            <select value={form.supportNeed} onChange={(event) => updateField('supportNeed', event.target.value)}>
                                {supportNeeds.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <div className="domain-grid">
                        {domainOptions.map((domain) => (
                            <button
                                type="button"
                                key={domain.value}
                                className={form.selectedDomain === domain.value ? 'selected' : ''}
                                onClick={() => updateField('selectedDomain', domain.value)}
                            >
                                <strong>{domain.label}</strong>
                                <span>{domain.alineaBrief || domain.hint}</span>
                            </button>
                        ))}
                    </div>
                </section>

                <section className="observador-card wide">
                    <div className="observador-section-title">
                        <h2>3. Una frase (opcional)</h2>
                        <p>
                            El sistema mira patrones breves en el texto (como «pero» o «por qué»); no archiva tu texto tal cual.
                            La cámara biométrica no se usa en esta pantalla (sí en el registro de jornada cuando tu empresa lo activa).
                        </p>
                    </div>

                    <p className="observador-chips-label">Atajos para tu frase (tocan y se añaden al cuadro)</p>
                    <div className="observador-reflection-chips">
                        {OBSERVADOR_REFLECTION_CHIPS.map((c) => (
                            <button
                                key={c.text}
                                type="button"
                                className={`observador-chip-btn${chipIsActive(c.text) ? ' selected' : ''}`}
                                onClick={() => appendReflectionChip(c.text)}
                                aria-pressed={chipIsActive(c.text)}
                            >
                                <span aria-hidden>{c.emoji}</span> {c.text}
                            </button>
                        ))}
                    </div>

                    <label className="reflection-field">
                        Tu frase (opcional)
                        <textarea
                            value={form.reflectionText}
                            onChange={(event) => updateField('reflectionText', event.target.value)}
                            rows={5}
                            placeholder="Unas líneas bastan…"
                        />
                    </label>

                    <div className="privacy-note">
                        Privacidad: sin audio en servidor, sin vídeo en servidor, sin transcripción completa guardada. Solo metadatos y
                        recomendaciones operativas.
                    </div>

                    {error && <div className="observador-error">{error}</div>}

                    <button className="observador-submit" type="submit" disabled={loading}>
                        {loading ? 'Un momento…' : 'Ver lectura'}
                    </button>
                </section>
            </form>

            {result && (
                <section className="observador-result">
                    <div className={`result-score level-${result.frictionLevel}`}>
                        <span>Lectura resumida</span>
                        <strong>{fmtRisk(result.riskScore)}</strong>
                        <p>
                            {result.loadDomainLabel} · {result.energyPattern}
                        </p>
                        {result.dailyCheckin?.checkinPhase && (
                            <p className="observador-checkin-note">
                                {checkinPhaseLabel(result.dailyCheckin.checkinPhase)}
                                {result.dailyCheckin.dailyCheckinComplete ? ' · Jornada completa hoy' : ''}
                            </p>
                        )}
                    </div>

                    <div className="result-grid">
                        <article>
                            <span>Patrón de lenguaje</span>
                            <strong>{result.languagePattern}</strong>
                            <p>
                                Y: {result.connectors.y} · Pero: {result.connectors.pero} · Por qué: {result.connectors.por_que}
                            </p>
                        </article>
                        <article>
                            <span>Para quien te acompaña</span>
                            <strong>{result.leaderGuide.action}</strong>
                            <p>{result.leaderGuide.question}</p>
                        </article>
                        <article>
                            <span>{result.epaCapsule.title}</span>
                            <strong>{result.epaCapsule.durationMinutes} minutos</strong>
                            <p>{result.epaCapsule.steps.join(' ')}</p>
                        </article>
                    </div>
                </section>
            )}
        </div>
    );
}
